import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { COMPANY_URL, ORDERS_URL, RECALL_RESPONSE } from "@/common/constants";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { KeyValueObj } from "@/common/types";
import { IAddedBatch, IProductBatches } from "@/common/types/others";
import { PageTitleNavs } from "@/common/components/page-title-navs";
import Stepper, { IStep } from "@/common/components/stepper";
import DeliveryInfo from "./delivery-info";
import Preview from "./preview";
import AutoScanner from "./scanner";
import { NewDeliveryWrapper } from "../new-delivery/style";
import useQuery from "@/hooks/useQuery";

interface IBatch {
  batch_number: string;
  quantity: number;
  inventory_id: string;
  brand_name: string;
  strength: string;
  strength_unit: string;
}

const steps: IStep[] = [
  { id: 1, label: "Batch Selection" },
  { id: 2, label: "Delivery information" },
  { id: 3, label: "Preview" },
];

const EMPTY_FORM = {
  batch_details: {} as IProductBatches,
  logistics_company: [],
  expected_delivery_date: "",
  recipent_phone_number: "",
  sender_phone_number: "",
  pickup_option: "",
  pickup_address: null,
  vehicle_needed: "",
  category: "",
  min_temperature: null,
  max_temperature: null,
  length: null,
  width: null,
  height: null,
  package_type: "",
};

function formatDateRange(deliveryDate: string): string {
  const format = (d: Date) =>
    [d.getDate(), d.getMonth() + 1, d.getFullYear()]
      .map((n) => String(n).padStart(2, "0"))
      .join("/");
  return `${format(new Date())}-${format(new Date(deliveryDate))}`;
}

function transformToProducts(data?: Record<string, IBatch[]> | null) {
  if (!data || typeof data !== "object") return [];
  return Object.entries(data).map(([atc_code_id, batching]) => ({
    atc_code_id,
    batching,
  }));
}

function transformDeliveryData(input: KeyValueObj) {
  return [
    {
      category: input.category,
      length: input.length || 0,
      width: input.width || 0,
      height: input.height || 0,
      package_type: input.package_type || "",
      min_temperature: input.min_temperature || "",
      max_temperature: input.max_temperature || "",
    },
  ];
}

function buildRecipientDetails(
  formData: KeyValueObj,
  productSource: KeyValueObj,
  manufacturerAddress: KeyValueObj,
  source: ISource,
) {
  const data =
    source === ISource.ORDER
      ? {
          id: productSource?.owner?.id,
          custom_address: {
            state: productSource?.address?.state,
            lga: productSource?.address?.lga,
            city: productSource?.address?.city,
            street: productSource?.address?.street,
            building_number: productSource?.address?.building_number,
          },
          secondary_phone_number: formData?.recipent_phone_number,
        }
      : {
          id: productSource?.recalled_product?.product?.owner_id,
          custom_address: {
            state: manufacturerAddress?.state,
            lga: manufacturerAddress?.lga,
            city: manufacturerAddress?.city,
            street: manufacturerAddress?.street,
            building_number: manufacturerAddress?.building_number,
          },
          secondary_phone_number:
            formData?.recipent_phone_number ||
            productSource?.owner?.phone_number,
        };
  return data;
}

function buildSenderDetails(formData: KeyValueObj) {
  return {
    secondary_phone_number: formData?.sender_phone_number,
    address_id: formData?.pickup_address?.id,
    custom_address: {
      state: formData?.pickup_address?.state,
      lga: formData?.pickup_address?.lga,
      city: formData?.pickup_address?.city,
      street: formData?.custom_address?.street,
      building_number: formData?.pickup_address?.building_number,
    },
  };
}

export enum ISource {
  RECALL = "recall",
  ORDER = "order",
}

const FROM_CONFIG: Record<
  NonNullable<ISource>,
  {
    link: string;
    extractProduct: (data: any) => any;
    dashboardLink: string;
    label: string;
    submissionLink: string;
  }
> = {
  order: {
    link: ORDERS_URL,
    extractProduct: (data) => data?.order_products?.[0],
    dashboardLink: "/dashboard/requisition",
    label: "Orders",
    submissionLink: ORDERS_URL + `/shipped/`,
  },
  recall: {
    link: RECALL_RESPONSE,
    extractProduct: (data) => data?.recalled_product?.product,
    dashboardLink: "/dashboard/recall",
    label: "Recall",
    submissionLink: RECALL_RESPONSE,
  },
};

const AutoDelivery = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") as ISource;
  const [loading, setLoading] = useState<boolean>(false);
  const sourceConfig = FROM_CONFIG[from];
  if (!from || !sourceConfig) {
    navigate(-1);
    return null;
  }

  const queryUrl = useMemo(() => {
    if (!from || !FROM_CONFIG[from]) return null;
    return `${FROM_CONFIG[from].link}/${id}`;
  }, [from, id]);

  const { data: deliverySourceData } = useQuery(queryUrl as string);
  const product =
    from === ISource.ORDER
      ? deliverySourceData?.order_products?.[0]
      : deliverySourceData?.recalled_product?.product;

  const apiCall = useApiCallHandling();
  const { data: recipientData } = useQuery(
    COMPANY_URL + `/${product?.owner_id}`,
    from === ISource.RECALL,
  );

  const manufacturerAddress = recipientData?.addresses?.[0];

  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([false, false, false]);

  const [formData, setFormData] = useState<KeyValueObj>(EMPTY_FORM);
  const [productBatches, setProductBatches] = useState<IProductBatches>({});

  const markCurrentStepComplete = () => {
    const productData =
      from === ISource.ORDER ? product?.product_details : product;
    setFormData((prev) => ({
      ...prev,
      category: productData?.storage_requirement?.split(" ").join("_"),
      package_type:
        productData?.tertiary_packaging ||
        productData?.secondary_packaging ||
        productData?.primary_packaging,
    }));
    setCompletedSteps((prev) => {
      const updated = [...prev];
      updated[currentStep] = true;
      return updated;
    });
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const goBack = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

  const handleStepChange = (index: number) => {
    if (index <= currentStep || completedSteps[index - 1]) {
      setCurrentStep(index);
    }
  };

  const handleProductSelected = (
    batches: IAddedBatch[],
    orderProduct: KeyValueObj,
    serials: string[],
  ) => {
    const updated = {
      ...productBatches,
      [orderProduct?.atc_code_id ?? orderProduct?.id]: batches,
    };
    setProductBatches(updated);
    setFormData((prev) => ({
      ...prev,
      batch_details: updated,
      scanned_serials: serials,
    }));

    markCurrentStepComplete();
  };

  const handleSubmit = () => {
    const payload = {
      estimated_delivery_date_range: formatDateRange(
        formData?.expected_delivery_date,
      ),
      products: transformToProducts(formData?.batch_details),
      logistics_company: formData?.logistics_company?.map((d: any) => d.id),
      preferred_vehicle_type: formData?.vehicle_needed,
      pickup_option: formData?.pickup_option,
      expected_delivery_date: formData?.expected_delivery_date,
      sender_details: buildSenderDetails(formData),
      recipient_details: buildRecipientDetails(
        formData,
        deliverySourceData,
        manufacturerAddress,
        from,
      ),
      delivery_items_details: transformDeliveryData(formData),
      scanned_serials: formData?.scanned_serials,
    };
    setLoading(true);
    apiCall(
      from === ISource.ORDER
        ? FROM_CONFIG[from]?.submissionLink + id
        : FROM_CONFIG[from]?.submissionLink + `/${id}/create-delivery-request`,
      "POST",
      payload,
      () => navigate(FROM_CONFIG[from]?.dashboardLink),
      console.log,
      () => setLoading(false),
    );
  };

  return (
    <NewDeliveryWrapper>
      <PageTitleNavs
        title="Auto Delivery"
        backLink={sourceConfig.dashboardLink}
        bcrumbs={[
          {
            title: sourceConfig?.label,
            link: sourceConfig?.dashboardLink,
          },
        ]}
      />

      <p className="text-sm text-gray-500 mt-1 mb-6">
        Set the batches, the corresponding quantity and the delivery details
      </p>
      <div className="main-wrapper">
        <Stepper
          steps={steps}
          currentStep={currentStep}
          onStepChange={handleStepChange}
          className="w-full"
        />

        <div className="mt-8 w-full">
          {currentStep === 0 && (
            <AutoScanner
              source={from as ISource}
              data={product}
              handleProductSelected={handleProductSelected}
            />
          )}

          {currentStep === 1 && (
            <DeliveryInfo
              onBack={goBack}
              formData={formData}
              setFormData={setFormData}
              onContinue={markCurrentStepComplete}
              productDetails={product?.product_details || product}
            />
          )}

          {currentStep === 2 && (
            <Preview
              productBatches={productBatches}
              formData={formData}
              setFormData={handleSubmit}
              onBack={goBack}
              loading={loading}
            />
          )}
        </div>
      </div>
    </NewDeliveryWrapper>
  );
};

export default AutoDelivery;
